import React from 'react';
import { usePageMeta, organizationSchema, breadcrumbSchema } from '../lib/seo';
import { siteConfig, legalLine } from '../siteConfig';
import { Container, PageHero, Prose, Section } from '../components/SiteUi';

const LegalLayout = ({ eyebrow, title, description, path, children }) => {
  usePageMeta({
    title: `${title} – CloudNext`,
    description,
    path,
    jsonLd: [organizationSchema(), breadcrumbSchema([{ name: 'Home', path: '/' }, { name: title, path }])],
  });

  return (
    <>
      <PageHero
        eyebrow={eyebrow}
        title={title}
        description={description}
        breadcrumbs={[{ name: 'Home', to: '/' }, { name: title }]}
        compact
      />
      <Section>
        <Container>
          <Prose>{children}</Prose>
          <p className="mt-10 text-xs leading-5 text-ash">{legalLine}</p>
        </Container>
      </Section>
    </>
  );
};

export function PrivacyPolicyPage() {
  return (
    <LegalLayout
      eyebrow="Legal"
      title="Privacy Policy"
      description="How CloudNext collects, uses and protects personal information, including website enquiries, chatbot conversations and call data."
      path="/privacy-policy"
    >
      <p>Last updated: November 2026.</p>
      <p>{legalLine} This policy explains how we handle personal information in plain English. It is not legal advice.</p>

      <h2>1. Who we are</h2>
      <p>CloudNext is an Australia-based software studio. You can contact us at <a href={`mailto:${siteConfig.email}`}>{siteConfig.email}</a>.</p>

      <h2>2. Information we collect</h2>
      <ul>
        <li>Contact details you give us: name, email, phone, business name, trade and location.</li>
        <li>Project information you share with us.</li>
        <li>Chatbot conversations on a website we operate or manage for a client.</li>
        <li>Call data for the AI receptionist, including call audio, transcripts, caller details and booking details where the service is enabled.</li>
        <li>Website analytics, such as pages visited, referrer and approximate location.</li>
      </ul>

      <h2>3. How we use information</h2>
      <ul>
        <li>To reply to enquiries and provide the service.</li>
        <li>To configure, test and improve the chatbot, AI receptionist and booking flow.</li>
        <li>To send confirmations, updates and service messages.</li>
        <li>To understand how the website is used and improve it.</li>
        <li>To meet legal and accounting obligations.</li>
      </ul>

      <h2>4. AI and call recording</h2>
      <p>Where an AI receptionist or chatbot is used, callers and visitors should be told they are interacting with an AI. Calls may be recorded only where consent is obtained or where the law allows it. A client can choose a non-recorded path or a callback where required.</p>
      <p>Call recordings and transcripts are used to provide the service, check quality and improve the setup. They are not sold.</p>

      <h2>5. Sharing and service providers</h2>
      <p>We use service providers for hosting, email, telephony, speech processing, AI models, analytics and CRM. They may process data in Australia or overseas. We do not sell personal information or share it with advertising networks.</p>

      <h2>6. Analytics</h2>
      <p>We use Google Analytics to understand how visitors use the site. You can opt out with the Google Analytics Opt-out Browser Add-on.</p>

      <h2>7. Security</h2>
      <p>We take reasonable steps to protect personal information from misuse, loss, unauthorised access and disclosure. No system is perfectly secure.</p>

      <h2>8. Retention</h2>
      <p>We keep personal information only as long as needed for the purpose it was collected, for the service agreement, or as required by law. Call recordings and transcripts are kept for the period set out in the service agreement or a shorter period where requested.</p>

      <h2>9. Your rights</h2>
      <p>You can ask for access to your personal information, ask us to correct it, or ask us to delete it where we are not required to keep it. Contact <a href={`mailto:${siteConfig.email}`}>{siteConfig.email}</a>.</p>

      <h2>10. Changes</h2>
      <p>We may update this policy. The current version is always on this page.</p>

      <h2>11. Contact</h2>
      <p>Questions about privacy can be sent to <a href={`mailto:${siteConfig.email}`}>{siteConfig.email}</a>.</p>
    </LegalLayout>
  );
}

export function TermsPage() {
  return (
    <LegalLayout
      eyebrow="Legal"
      title="Terms of Use"
      description="The terms that apply to the CloudNext website and services."
      path="/terms-of-use"
    >
      <p>Last updated: November 2026.</p>
      <p>These terms apply to the CloudNext website and to services provided by CloudNext. A separate written agreement or quote may apply to a project.</p>

      <h2>1. Acceptance</h2>
      <p>By using this website or engaging CloudNext, you agree to these terms. If you do not agree, do not use the site or the services.</p>

      <h2>2. Services</h2>
      <p>CloudNext provides websites, website chatbots, AI receptionists, lead capture, appointment booking and custom software. The scope of each engagement is set out in a written quote or agreement.</p>

      <h2>3. Quotes and payment</h2>
      <p>Quotes are based on the agreed scope. Work outside the scope may require a new quote. Payment terms, deposits, milestones and recurring fees are set out in the quote or agreement. Late payment may pause work or the service.</p>

      <h2>4. Client responsibilities</h2>
      <p>You are responsible for providing accurate business information, approved pricing rules, call flows, escalation contacts, calendar or job-system access, and any consent required for call recording or messaging. You are responsible for the legality of your own offers and claims.</p>

      <h2>5. AI service limitations</h2>
      <p>AI systems are assistive. They can make mistakes, misunderstand a caller or be unavailable. CloudNext tests the setup, but does not guarantee that every call is answered perfectly or that every enquiry becomes a booking. The AI must not be used for emergency, medical, legal or financial advice. For an emergency, call 000.</p>

      <h2>6. Intellectual property</h2>
      <p>Ownership of code, content and deliverables is set out in the written agreement. Unless agreed otherwise, CloudNext retains the right to use the work in its portfolio, with client permission where required.</p>

      <h2>7. Confidentiality</h2>
      <p>Both parties agree to keep confidential information private and use it only for the engagement. We can sign an NDA before discussing sensitive details.</p>

      <h2>8. Privacy</h2>
      <p>Our privacy policy and AI and data policy explain how personal information is handled.</p>

      <h2>9. Liability</h2>
      <p>To the extent permitted by law, CloudNext's liability is limited to the amount paid for the service giving rise to the claim. We are not liable for indirect or consequential loss.</p>

      <h2>10. Termination</h2>
      <p>Either party may terminate in accordance with the written agreement. You remain responsible for work performed and costs incurred up to the termination date.</p>

      <h2>11. Governing law</h2>
      <p>These terms are governed by the laws of Queensland, Australia.</p>

      <h2>12. Contact</h2>
      <p>Questions about these terms can be sent to <a href={`mailto:${siteConfig.email}`}>{siteConfig.email}</a>.</p>
    </LegalLayout>
  );
}

export function AiDataPolicyPage() {
  return (
    <LegalLayout
      eyebrow="AI and data"
      title="AI and Data Policy"
      description="How CloudNext handles data for AI receptionists, website chatbots and appointment booking."
      path="/ai-data-policy"
    >
      <p>Last updated: November 2026.</p>
      <p>This page explains how we handle data for the AI receptionist and chatbot services. It is a plain-English summary, not legal advice. The written service agreement prevails.</p>

      <h2>1. What we collect</h2>
      <ul>
        <li>Caller and visitor details: name, phone, email, business, suburb and the reason for the call.</li>
        <li>Call audio, transcripts and summaries where recording is enabled.</li>
        <li>Chat transcripts and lead details.</li>
        <li>Booking details and calendar or job-system references.</li>
        <li>Technical logs needed to run and secure the service.</li>
      </ul>

      <h2>2. AI disclosure</h2>
      <p>Callers should be told they are speaking to an AI at the start of the call. The client approves the wording. If the caller asks for a human, the setup should transfer or take a message according to the client's rules.</p>

      <h2>3. Call recording and consent</h2>
      <p>Calls are recorded only where consent is obtained or where the law allows it. The client decides whether recording is on. A non-recorded path or callback should be available where required. The client is responsible for its own recording notices and consent wording.</p>

      <h2>4. How data is used</h2>
      <ul>
        <li>To answer the call or chat and complete the requested action.</li>
        <li>To book, reschedule or cancel an appointment.</li>
        <li>To send a summary or notification to the business.</li>
        <li>To check quality, fix issues and improve the setup.</li>
        <li>To meet legal and accounting obligations.</li>
      </ul>

      <h2>5. Service providers</h2>
      <p>We use providers for telephony, speech-to-text, text-to-speech, AI models, hosting, email, CRM and analytics. They may process data in Australia or overseas. The current provider list and processing locations are available on request. We do not sell personal information.</p>

      <h2>6. Retention</h2>
      <p>We keep call recordings, transcripts and chat data for the period set out in the service agreement, or a shorter period where requested and technically possible. We delete or de-identify data when it is no longer needed, unless we are required to keep it.</p>

      <h2>7. Access and security</h2>
      <p>Access is limited to people who need it to provide the service. We use access controls, secure storage and reasonable technical measures. No system is perfectly secure.</p>

      <h2>8. Your rights</h2>
      <p>You can ask for access to your personal information, ask for a correction, or ask for deletion where we are not required to keep it. Contact <a href={`mailto:${siteConfig.email}`}>{siteConfig.email}</a>.</p>

      <h2>9. Client responsibilities</h2>
      <p>The client is responsible for the accuracy of its service information, pricing rules and escalation contacts, for its own privacy notices, and for any consent required for recording, SMS or marketing. Do not use the AI for emergency, medical, legal or financial advice.</p>

      <h2>10. Contact</h2>
      <p>Questions about AI and data can be sent to <a href={`mailto:${siteConfig.email}`}>{siteConfig.email}</a>.</p>
    </LegalLayout>
  );
}
